import { useCallback, useEffect, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

export default function useIntegrationsManagement() {
  const isMounted = useIsMounted();
  const [enabled, setEnabled] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/integrations`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load integrations');
      }
      const json = await res.json();
      setEnabled(Array.isArray(json.enabled) ? json.enabled : []);
      setAvailable(Array.isArray(json.available) ? json.available : []);
    } catch (err) {
      if (!isMounted.current) return;
      setError(err);
      setEnabled([]);
      setAvailable([]);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [isMounted]);

  const fetchDetail = useCallback(async (integrationId) => {
    if (!integrationId) {
      setDetail(null);
      return null;
    }

    setDetailLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/integrations/${integrationId}`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load integration details');
      }
      const json = await res.json();
      setDetail(json);
      return json;
    } catch (err) {
      if (!isMounted.current) return;
      setActionError(err.message || 'Failed to load integration details');
      setDetail(null);
      return null;
    } finally {
      if (!isMounted.current) return;
      setDetailLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (selectedIntegrationId) {
      fetchDetail(selectedIntegrationId);
    } else {
      setDetail(null);
    }
  }, [selectedIntegrationId, fetchDetail]);

  const updateIntegration = useCallback(async (integrationId, patch, credentials) => {
    setActionLoadingId(integrationId);
    setActionError('');
    try {
      const body = { ...patch };
      if (credentials && Object.keys(credentials).length > 0) {
        body.credentials = credentials;
      }

      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/integrations/${integrationId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.errors?.join(', ') || data.error || 'Update failed');
      }
      const json = await res.json();
      setDetail(json);
      await reload();
      return json;
    } catch (err) {
      if (!isMounted.current) return;
      setActionError(err.message || 'Update failed');
      throw err;
    } finally {
      if (!isMounted.current) return;
      setActionLoadingId(null);
    }
  }, [reload, isMounted]);

  const createIntegration = useCallback(async ({ type, fields, credentials }) => {
    setActionError('');
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/integrations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type,
            ...fields,
            credentials,
          }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.errors?.join(', ') || data.error || 'Create failed');
      }
      const json = await res.json();
      await reload();
      return json;
    } catch (err) {
      if (!isMounted.current) return;
      setActionError(err.message || 'Create failed');
      throw err;
    }
  }, [reload, isMounted]);

  const toggleSyncEnabled = useCallback(async (integration, nextValue) => {
    return updateIntegration(integration.id, { sync_enabled: nextValue });
  }, [updateIntegration]);

  const triggerManualSync = useCallback(async (integrationId) => {
    setActionLoadingId(integrationId);
    setActionError('');
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/integrations/${integrationId}/sync`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Sync failed');
      }
      const json = await res.json();
      setDetail(json);
      await reload();
      return json;
    } catch (err) {
      if (!isMounted.current) return;
      setActionError(err.message || 'Sync failed');
      if (selectedIntegrationId === integrationId) {
        await fetchDetail(integrationId);
      }
      throw err;
    } finally {
      if (!isMounted.current) return;
      setActionLoadingId(null);
    }
  }, [reload, selectedIntegrationId, fetchDetail, isMounted]);

  return {
    enabled,
    available,
    loading,
    error,
    actionLoadingId,
    actionError,
    clearActionError: () => setActionError(''),
    reload,
    toggleSyncEnabled,
    triggerManualSync,
    selectedIntegrationId,
    setSelectedIntegrationId,
    detail,
    detailLoading,
    fetchDetail,
    updateIntegration,
    createIntegration,
  };
}
